import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Briefcase, PlusCircle } from 'lucide-react';
import { engagementsApi } from '../lib/api-client';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input, Label } from '../components/ui/input';
import { Dialog, DialogContent, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { LoadingState, ErrorState, EmptyState, friendlyErrorMessage } from '../components/ui/states';
import { useToast } from '../components/ui/toast';
import { can } from '../lib/rbac';
import { useAuth } from '../lib/auth-context';
import type { EngagementStatus } from '../lib/types';

const STATUS_LABEL: Record<EngagementStatus, string> = {
  draft: 'Draft',
  awaiting_authorization: 'Waiting for authorization',
  scheduled: 'Scheduled',
  running: 'Running',
  paused: 'Paused',
  completed: 'Completed',
  aborted: 'Aborted',
};

const STATUS_VARIANT: Record<EngagementStatus, 'neutral' | 'warning' | 'success' | 'brand' | 'danger'> = {
  draft: 'neutral',
  awaiting_authorization: 'warning',
  scheduled: 'brand',
  running: 'brand',
  paused: 'warning',
  completed: 'success',
  aborted: 'danger',
};

const ALWAYS_OPEN_WINDOW = [{ days: [0, 1, 2, 3, 4, 5, 6], startMinute: 0, endMinute: 1440, tzOffsetMinutes: 0 }];

function CreateEngagementForm({ onDone }: { onDone: () => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => engagementsApi.create({ name, scanWindows: ALWAYS_OPEN_WINDOW }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['engagements'] });
      toast({ title: 'Engagement created', description: 'Next: authorize it and set its scope.', variant: 'success' });
      onDone();
    },
    onError: (err) => setError(friendlyErrorMessage(err)),
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div className="space-y-1">
        <Label htmlFor="engagement-name">Name</Label>
        <Input id="engagement-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Q3 external pentest" required />
        <p className="text-xs text-ink-muted">
          Testing runs anytime by default — you can restrict this to specific hours later in the engagement's settings.
        </p>
      </div>
      {error ? <p className="text-xs text-danger">{error}</p> : null}
      <Button type="submit" className="w-full" disabled={mutation.isPending || !name.trim()}>
        Create engagement
      </Button>
    </form>
  );
}

export function Engagements() {
  const { principal } = useAuth();
  const canWrite = principal?.role === undefined || can(principal.role, 'engagement:write');
  const [createOpen, setCreateOpen] = useState(false);
  const query = useQuery({ queryKey: ['engagements'], queryFn: () => engagementsApi.list() });

  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const engagements = query.data?.engagements ?? [];

  return (
    <div>
      <PageHeader
        title="Engagements"
        description="An engagement ties together authorization, scope, and the runs you start against it."
        action={
          canWrite ? (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button>
                  <PlusCircle size={16} /> New engagement
                </Button>
              </DialogTrigger>
              <DialogContent title="New engagement" description="Give it a name — you'll set authorization and scope next.">
                <CreateEngagementForm onDone={() => setCreateOpen(false)} />
              </DialogContent>
            </Dialog>
          ) : undefined
        }
      />

      {engagements.length === 0 ? (
        <EmptyState icon={<Briefcase size={32} />} title="No engagements yet" description="Create one to start authorizing and testing." />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {engagements.map((e) => (
            <Link key={e.id} to={`/engagements/${e.id}`}>
              <Card className="transition-shadow hover:shadow-md">
                <CardContent className="pt-6">
                  <p className="font-medium text-ink">{e.name}</p>
                  <Badge variant={STATUS_VARIANT[e.status]} className="mt-2">
                    {STATUS_LABEL[e.status]}
                  </Badge>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export { STATUS_LABEL, STATUS_VARIANT };
